import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, Sparkles } from "lucide-react";
import Logo from "@/components/Logo";

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "Programs", path: "/programs" },
    { name: "Offerings Hub", path: "/offerings" },
    { name: "About ESHA", path: "/about" },
    { name: "Contact & FAQ", path: "/contact" }
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-[#050505]/90 border-b border-[#FF8C00]/15 py-3 shadow-[0_4px_30px_rgba(0,0,0,0.5)]"
          : "bg-transparent py-5"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 flex items-center justify-between">
        {/* Brand Logo */}
        <Link to="/" className="focus-visible:outline-none" data-testid="header-logo-link">
          <Logo className="h-10 sm:h-12" />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-8" aria-label="Main Navigation">
          {navLinks.map((link) => {
            const isActive = location.pathname === link.path;
            return (
              <Link
                key={link.path}
                to={link.path}
                className={`relative font-sans text-xs tracking-widest uppercase transition-colors duration-300 py-1 hover:text-[#00D2FF] font-bold ${
                  isActive ? "text-[#00D2FF]" : "text-[#AAA29E]"
                }`}
                data-testid={`header-navigation-link-${link.name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {link.name}
                {isActive && (
                  <motion.span
                    layoutId="activeNavIndicator"
                    className="absolute bottom-0 left-0 right-0 h-[1.5px] bg-gradient-to-r from-transparent via-[#00D2FF] to-transparent"
                    transition={{ type: "spring", stiffness: 380, damping: 30 }}
                  />
                )}
              </Link>
            );
          })}
        </nav>

        {/* Desktop Action CTAs */}
        <div className="hidden sm:flex items-center gap-4">
          <a
            href="https://learn.eshawitheswari.com/web/lite/events/6a48df41599e3232f7dee506"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs uppercase tracking-widest text-[#AAA29E] hover:text-[#00D2FF] transition-all duration-300 flex items-center gap-1.5 px-3 py-2 font-mono font-bold focus-visible:ring-1 focus-visible:ring-[#00D2FF] rounded-sm"
            data-testid="header-cta-clarity-call-button"
          >
            Clarity Call
            <ArrowUpRight className="h-3 w-3" />
          </a>
          <a
            href="https://learn.eshawitheswari.com/l/96a7c7eae0"
            target="_blank"
            rel="noopener noreferrer"
            className="relative overflow-hidden group px-5 py-2.5 bg-gradient-to-r from-[#FF8C00] to-[#FF4500] text-[#F6F4EE] text-xs uppercase tracking-widest font-mono font-bold rounded-full transition-all duration-300 shadow-[0_4px_15px_rgba(255,140,0,0.3)] hover:shadow-[0_6px_25px_rgba(255,140,0,0.5)] hover:scale-105 active:scale-95"
            data-testid="header-cta-masterclass-button"
          >
            <span className="relative z-10 flex items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5 animate-pulse" />
              Attend Masterclass
            </span>
            <div className="absolute inset-0 bg-gradient-to-r from-[#FFD700] to-[#FF8C00] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          </a>
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="lg:hidden p-2 text-[#F6F4EE] hover:text-[#00D2FF] transition-colors focus-visible:outline-none"
          aria-label="Toggle Navigation Menu"
          aria-expanded={isOpen}
          data-testid="header-mobile-menu-button"
        >
          {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile Drawer Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-x-0 top-[65px] bg-[#050505]/95 border-b border-[#FF8C00]/15 backdrop-blur-2xl shadow-2xl py-8 px-6 flex flex-col lg:hidden gap-6 z-40 max-h-[85vh] overflow-y-auto"
            data-testid="mobile-navigation-drawer"
          >
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => {
                const isActive = location.pathname === link.path;
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`font-sans text-base tracking-widest uppercase transition-colors duration-300 border-b border-[#FF8C00]/5 pb-2.5 font-bold ${
                      isActive ? "text-[#00D2FF]" : "text-[#AAA29E]"
                    }`}
                    data-testid={`header-mobile-link-${link.name.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {link.name}
                  </Link>
                );
              })}
            </div>

            <div className="flex flex-col gap-4 mt-4">
              <a
                href="https://learn.eshawitheswari.com/l/96a7c7eae0"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-[#FF8C00] to-[#FF4500] text-[#F6F4EE] text-sm uppercase tracking-wider font-mono font-bold rounded-full text-center hover:scale-[1.02] active:scale-95 transition-all"
                data-testid="header-mobile-cta-masterclass-button"
              >
                <Sparkles className="h-4 w-4" />
                Attend Masterclass
              </a>
              <a
                href="https://learn.eshawitheswari.com/web/lite/events/6a48df41599e3232f7dee506"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full py-3 border border-[#FF8C00]/25 hover:border-[#FF8C00]/50 text-[#F6F4EE] text-sm uppercase tracking-wider font-mono font-bold rounded-full text-center hover:scale-[1.02] active:scale-95 transition-all bg-[#111111]"
                data-testid="header-mobile-cta-clarity-call-button"
              >
                Book a Clarity Call
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
